import { useEffect, useState } from "react";
import apiClient from "@/services/apiClient";
import DataListPage from "./DataListPage";
import ToastHelper from "@/utils/toastHelper";

export default function AdminClasses() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lookups, setLookups] = useState({ courses: [], campuses: [], trainers: [] });

  const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");

  const mapClass = (item) => ({
    id: item.scheduleCode || `C-${item.courseScheduleId}`,
    course: item.courseName,
    date: formatScheduleDate(item.scheduleDate),
    time: `${item.startTime || "-"} - ${item.endTime || "-"}`,
    location: item.campusName,
    trainer: item.trainerName,
    booked: Number(item.bookedSeats || 0),
    capacity: Number(item.maxSeats || 0),
    status: Number(item.statusNo) === 1 ? "Active" : Number(item.statusNo) === 2 ? "InActive" : "None",
    raw: item,
  });

  useEffect(() => {
    const loadClasses = async () => {
      try {
        const response = await apiClient.get(`${apiBaseUrl}/AdminClass/GetClasses`);
        const classes = Array.isArray(response.data?.classes) ? response.data.classes : [];
        setRows(classes.map(mapClass));
        setLookups({
          courses: Array.isArray(response.data?.courses) ? response.data.courses : [],
          campuses: Array.isArray(response.data?.campuses) ? response.data.campuses : [],
          trainers: Array.isArray(response.data?.trainers) ? response.data.trainers : [],
        });
      } catch (requestError) {
        console.error("Failed to load classes", requestError);
        setError(requestError.response?.data?.message || "Unable to load classes.");
      }
    };
    loadClasses();
  }, [apiBaseUrl]);

  const openDialog = (row, mode) => setDialog({ mode, item: { ...row.raw } });
  const openAddDialog = () => {
    const selectedDate = getLocalDateValue(new Date());
    setDialog({ mode: "add", item: {
      courseScheduleId: 0,
      courseId: lookups.courses[0]?.id || 0,
      campusId: lookups.campuses[0]?.id || 0,
      trainerId: lookups.trainers[0]?.id || 0,
      scheduleDate: toLocalDateTimeOffset(selectedDate),
      scheduleCode: scheduleCodeFromDate(selectedDate),
      startTime: "08:30", endTime: "15:30", maxSeats: 100, bookedSeats: 0,
      courseFee: Number(lookups.courses[0]?.fee || 0), status: "Active", statusNo: 1, remarks: "",
      courseName: "", campusName: "", trainerName: "",
    }});
  };
  const updateField = (key, value) => setDialog((current) => ({ ...current, item: { ...current.item, [key]: value } }));
  const changeStatus = async (row, statusNo) => {
    const item = { ...row.raw, statusNo, status: statusNo === 1 ? "Active" : "InActive" };
    setSaving(true);
    try {
      await apiClient.put(`${apiBaseUrl}/AdminClass/${item.courseScheduleId}`, item);
      setRows((current) => current.map((currentRow) => currentRow.raw.courseScheduleId === item.courseScheduleId ? mapClass(item) : currentRow));
      ToastHelper.success(`Class marked ${item.status}`);
    } catch (requestError) {
      ToastHelper.apiError(requestError, "Unable to change class status");
    } finally {
      setSaving(false);
    }
  };

  const saveClass = async () => {
    setSaving(true);
    try {
      if (dialog.mode === "add") {
        const response = await apiClient.post(`${apiBaseUrl}/AdminClass`, dialog.item);
        const created = response.data;
        created.courseName = lookups.courses.find((x) => x.id === created.courseId)?.name || "";
        created.campusName = lookups.campuses.find((x) => x.id === created.campusId)?.name || "";
        created.trainerName = lookups.trainers.find((x) => x.id === created.trainerId)?.name || "";
        setRows((current) => [...current, mapClass(created)]);
        ToastHelper.success("Class scheduled successfully");
      } else {
        await apiClient.put(`${apiBaseUrl}/AdminClass/${dialog.item.courseScheduleId}`, dialog.item);
        setRows((current) => current.map((row) => row.raw.courseScheduleId === dialog.item.courseScheduleId ? mapClass(dialog.item) : row));
        ToastHelper.success("Class updated successfully");
      }
      setDialog(null);
    } catch (requestError) {
      ToastHelper.apiError(requestError, "Unable to update class");
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;

  return <>
  <DataListPage
    title="Classes" subtitle="Scheduled classes across all locations" testidPrefix="classes"
    rows={rows}
    statusStyles={{ Active: "bg-emerald-50 text-emerald-700 border border-emerald-200", InActive: "bg-rose-50 text-rose-700 border border-rose-200", None: "bg-slate-100 text-slate-700 border border-slate-200" }}
    addButton="+ Schedule class"
    onAdd={openAddDialog}
    actions={[
      { label: "View", fn: (row) => openDialog(row, "view") },
      { label: "Edit", fn: (row) => openDialog(row, "edit") },
      { label: "Activate", fn: (row) => changeStatus(row, 1), hidden: (row) => row.status === "Active" || saving },
      { label: "Deactivate", fn: (row) => changeStatus(row, 2), hidden: (row) => row.status === "InActive" || saving },
    ]}
    columns={[
      { key: "id", label: "Class ID" },
      { key: "course", label: "Course" },
      { key: "date", label: "Date" },
      { key: "time", label: "Time" },
      { key: "location", label: "Location", render: (r) => <span className="font-semibold">{r.location}</span> },
      { key: "trainer", label: "Trainer" },
      { key: "booked", label: "Booked / Cap", align: "right", render: (r) => `${r.booked}/${r.capacity}` },
      { key: "status", label: "Status", type: "status" },
    ]}
  />
  {dialog && <ClassDialog dialog={dialog} lookups={lookups} onClose={() => setDialog(null)} onChange={updateField} onSave={saveClass} saving={saving} />}
  </>;
}

function ClassDialog({ dialog, lookups, onClose, onChange, onSave, saving }) {
  const item = dialog.item;
  const editing = dialog.mode === "edit" || dialog.mode === "add";
  const dateValue = getDatePart(item.scheduleDate);
  const fields = [
    ["scheduleCode", "Schedule code", "text"],
    ["scheduleDate", "Schedule date", "date"], ["startTime", "Start time", "time"],
    ["endTime", "End time", "time"], ["maxSeats", "Maximum seats", "number"],
    ["bookedSeats", "Booked seats", "number"], ["courseFee", "Course fee", "number"],
  ];
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl">
      <div className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
        <div><h2 className="text-xl font-display font-extrabold text-[#0A2540]">{dialog.mode === "add" ? "Schedule class" : editing ? "Edit class" : "Class details"}</h2><p className="text-sm text-slate-500">{dialog.mode === "add" ? "Create a new course schedule" : `${item.courseName} · ${item.campusName}`}</p></div>
        <button className="text-2xl text-slate-400 hover:text-slate-700" onClick={onClose}>×</button>
      </div>
      <div className="grid gap-4 p-6 sm:grid-cols-2">
        <LookupField label="Course" value={item.courseId} options={lookups.courses} disabled={!editing} onChange={(value) => { onChange("courseId", value); onChange("courseFee", Number(lookups.courses.find(option => option.id === value)?.fee || 0)); }} />
        <LookupField label="Campus" value={item.campusId} options={lookups.campuses} disabled={!editing} onChange={(value) => onChange("campusId", value)} />
        <LookupField label="Trainer" value={item.trainerId} options={lookups.trainers} disabled={!editing} onChange={(value) => onChange("trainerId", value)} />
        <LookupField label="Status" value={item.statusNo} options={[{ id: 0, name: "None" }, { id: 1, name: "Active" }, { id: 2, name: "InActive" }]} disabled={!editing} onChange={(value) => { onChange("statusNo", value); }} />
        {fields.map(([key, label, type]) => <label key={key} className="block"><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">{label}</span><input className="wc-input disabled:bg-slate-100 disabled:text-slate-600" type={type} min={key === "maxSeats" ? 1 : undefined} max={key === "maxSeats" ? 100 : undefined} disabled={!editing || key === "scheduleCode" || key === "courseFee"} readOnly={key === "scheduleCode" || key === "courseFee"} step={key === "courseFee" ? "0.01" : undefined} value={key === "scheduleDate" ? dateValue : (item[key] ?? "")} onChange={(e) => { if (key === "scheduleDate") { const code = scheduleCodeFromDate(e.target.value); onChange("scheduleDate", toLocalDateTimeOffset(e.target.value)); onChange("scheduleCode", code); } else onChange(key, type === "number" ? Number(e.target.value) : e.target.value); }} /></label>)}
        <label className="block sm:col-span-2"><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Remarks</span><textarea className="wc-input min-h-24" disabled={!editing} value={item.remarks || ""} onChange={(e) => onChange("remarks", e.target.value)} /></label>
      </div>
      <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-white px-6 py-4"><button className="wc-btn-outline" onClick={onClose}>Close</button>{editing && <button className="wc-btn-primary" disabled={saving} onClick={onSave}>{saving ? "Saving..." : "Save changes"}</button>}</div>
    </div>
  </div>;
}

function LookupField({ label, value, options, disabled, onChange }) {
  return <label className="block"><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">{label}</span><select className="wc-input" disabled={disabled} value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}><option value="">Select {label.toLowerCase()}</option>{options.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}</select></label>;
}


function getDatePart(value) {
  if (!value) return "";

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
}

function getLocalDateValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function toLocalDateTimeOffset(dateValue) {
  if (!dateValue) return null;

  const [year, month, day] = dateValue.split("-").map(Number);
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  const offsetMinutes = -localDate.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffset / 60)).padStart(2, "0");
  const offsetMins = String(absoluteOffset % 60).padStart(2, "0");

  return `${dateValue}T00:00:00${sign}${offsetHours}:${offsetMins}`;
}

function formatScheduleDate(value) {
  const dateValue = getDatePart(value);
  if (!dateValue) return "-";

  const [year, month, day] = dateValue.split("-");
  return `${day}/${month}/${year}`;
}

function scheduleCodeFromDate(date) {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `WC-${day}${month}${year.slice(-2)}`;
}
